import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import SubmitCompleteLurePackage from './submissions/SubmitCompleteLurePackage'

/**
 * UserSubmissionsHub Component
 * 
 * Simplified submission interface - Complete Lure Package only.
 * Users can submit brand, lure, color, and variant all in one form.
 * 
 * Daily Limit: 10 complete packages per day
 */
function UserSubmissionsHub() {
  const { user } = useAuth()
  const navigate = useNavigate()

  if (!user) {
    navigate('/login')
    return null
  }

  return (
    <div className="user-submissions-hub">
      {/* Header */}
      <div style={{ 
        textAlign: 'center', 
        marginBottom: '2rem',
        padding: '2rem',
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <h1 style={{ color: '#1e3a8a', fontSize: '2.5rem', marginBottom: '0.5rem' }}>
          📦 Submit a Complete Lure Package
        </h1>
        <p style={{ color: '#64748b', fontSize: '1.1rem', maxWidth: '800px', margin: '0 auto' }}>
          Our streamlined submission process lets you contribute a complete lure entry in one go. 
          Submit brand, lure details, color, and variant information all at once for faster approval!
        </p>
      </div>

      {/* Benefits Section */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
          padding: '1.5rem',
          borderRadius: '12px',
          border: '2px solid #3b82f6',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>⚡</div>
          <div style={{ fontWeight: 'bold', color: '#1e3a8a', marginBottom: '0.5rem' }}>
            Fast & Easy
          </div>
          <div style={{ color: '#475569', fontSize: '0.9rem' }}>
            Submit everything in one convenient form
          </div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
          padding: '1.5rem',
          borderRadius: '12px',
          border: '2px solid #10b981',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🚀</div>
          <div style={{ fontWeight: 'bold', color: '#065f46', marginBottom: '0.5rem' }}>
            Quick Approval
          </div>
          <div style={{ color: '#475569', fontSize: '0.9rem' }}>
            Everything reviewed together by our admin team
          </div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
          padding: '1.5rem',
          borderRadius: '12px',
          border: '2px solid #f59e0b',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📊</div>
          <div style={{ fontWeight: 'bold', color: '#92400e', marginBottom: '0.5rem' }}>
            10 Per Day
          </div>
          <div style={{ color: '#475569', fontSize: '0.9rem' }}>
            Submit up to 10 complete packages daily
          </div>
        </div>
      </div>

      {/* What You'll Submit */}
      <div style={{
        background: 'white',
        padding: '2rem',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        marginBottom: '2rem'
      }}>
        <h2 style={{ color: '#1e3a8a', marginBottom: '1rem', textAlign: 'center' }}>
          📋 What's Included in a Complete Package
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          marginTop: '1.5rem'
        }}>
          <div style={{ padding: '1rem', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🏭</div>
            <div style={{ fontWeight: 'bold', color: '#1e3a8a' }}>Brand Info</div>
            <div style={{ color: '#64748b', fontSize: '0.9rem' }}>
              New or existing brand
            </div>
          </div>
          
          <div style={{ padding: '1rem', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🎣</div>
            <div style={{ fontWeight: 'bold', color: '#1e3a8a' }}>Lure Details</div>
            <div style={{ color: '#64748b', fontSize: '0.9rem' }}>
              Type, size, specifications
            </div>
          </div>
          
          <div style={{ padding: '1rem', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🎨</div>
            <div style={{ fontWeight: 'bold', color: '#1e3a8a' }}>Color Info</div>
            <div style={{ color: '#64748b', fontSize: '0.9rem' }}>
              Official color name & details
            </div>
          </div>
          
          <div style={{ padding: '1rem', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🔀</div>
            <div style={{ fontWeight: 'bold', color: '#1e3a8a' }}>Variant</div>
            <div style={{ color: '#64748b', fontSize: '0.9rem' }}>
              Specific color variant details
            </div>
          </div>
        </div>
      </div>

      {/* The Form */}
      <SubmitCompleteLurePackage />

      {/* Guidelines Section */}
      <div style={{
        background: 'white',
        padding: '2rem',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        marginTop: '2rem'
      }}>
        <h2 style={{ color: '#1e3a8a', marginBottom: '1rem' }}>
          💡 Submission Tips
        </h2>
        <ul style={{ color: '#475569', lineHeight: '1.8' }}>
          <li>
            <strong>Search First:</strong> Check if the lure already exists in our database to avoid duplicates
          </li>
          <li>
            <strong>Official Names:</strong> Use the manufacturer's official names for lures and colors (e.g., "Sexy Shad", "Ghost Minnow")
          </li>
          <li>
            <strong>Be Complete:</strong> Fill in as many details as possible - the more info, the better!
          </li>
          <li>
            <strong>Double Check:</strong> Review all information before submitting
          </li>
          <li>
            <strong>Include Specs:</strong> Weight, length, and depth range help other anglers find the right lure
          </li>
          <li>
            <strong>Limited Editions:</strong> Mark colors as limited edition if they were special releases
          </li>
          <li>
            <strong>Wait for Approval:</strong> Our admin team typically reviews submissions within 24-48 hours
          </li>
        </ul>
      </div>

      {/* Need Help Section */}
      <div style={{
        background: 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)',
        padding: '1.5rem',
        borderRadius: '12px',
        border: '2px solid #0ea5e9',
        marginTop: '2rem',
        textAlign: 'center'
      }}>
        <h3 style={{ margin: '0 0 0.5rem 0', color: '#0c4a6e' }}>
          ❓ Need Help?
        </h3>
        <p style={{ margin: 0, color: '#075985' }}>
          Not sure if a lure exists already? <a href="/browse" style={{ color: '#0284c7', fontWeight: 'bold' }}>Browse our database</a> first.
          Check your submission history on <a href="/my-submissions" style={{ color: '#0284c7', fontWeight: 'bold' }}>My Submissions</a> page.
        </p>
      </div>
    </div>
  )
}

export default UserSubmissionsHub